/**
 * Property Field Rules Utility
 *
 * This utility handles the extraction and filtering of property field rules
 * from survey configuration data. It provides a consistent way to determine
 * which property fields should be displayed across different views.
 */

/**
 * Interface for property field rules from survey configuration
 * Defines how property fields should be displayed in the UI
 */
export interface PropertyFieldRule {
  fieldId: string;
  fieldKey: string | null;
  fieldLabel: string;
  status: "ACTIVE" | "INACTIVE";
  sortOrder?: number;
  order?: number; // API uses 'order' field
  isHidden?: boolean;
}

/**
 * Survey type with property field rules (using generic approach for compatibility)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SurveyWithFieldRules = any;

/**
 * Fields that should always be excluded from property field displays
 */
const EXCLUDED_FIELDS = [
  "name",
  "address",
  "city",
  "state province",
  "postal code",
];

/**
 * Options for filtering property fields
 */
export interface PropertyFieldFilterOptions {
  /** Whether to exclude address/identity fields (default: true for comparison view) */
  excludeAddressFields?: boolean;
  /** Additional fields to exclude */
  additionalExcludedFields?: string[];
  /** Whether address line 2 has a custom value; if false, 'address2' is excluded */
  hasAddressLine2Custom?: boolean;
}

/**
 * Extract and filter active property fields from survey configuration
 *
 * @param survey - Survey data that may contain property field rules
 * @param options - Filtering options
 * @returns Array of active property field rules, sorted by order
 */
export function getActivePropertyFields(
  survey: SurveyWithFieldRules | null | undefined,
  options: PropertyFieldFilterOptions = {},
): PropertyFieldRule[] {
  const {
    excludeAddressFields = false,
    additionalExcludedFields = [],
    hasAddressLine2Custom = true,
  } = options;

  // Access field rules from survey configuration
  let fieldRules =
    survey?.surveyPropertyFieldRule?.propertyFieldRuleList ||
    survey?.propertyFieldRuleList;

  // Return empty array if fieldRules is still null/undefined
  if (!fieldRules) {
    return [];
  }

  // Combine excluded fields
  const excludedFields = [
    ...(excludeAddressFields ? EXCLUDED_FIELDS : []),
    ...additionalExcludedFields,
    ...(hasAddressLine2Custom === false ? ["address line 2"] : []),
  ].map((field) => field.toLowerCase());

  // Filter for active fields that are not hidden and sort by sortOrder/order
  return (
    fieldRules
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((rule: any): rule is PropertyFieldRule => {
        return (
          rule !== null &&
          typeof rule === "object" &&
          (!("status" in rule) || rule.status === "ACTIVE") &&
          rule.isHidden !== true &&
          !excludedFields.includes(rule.fieldLabel.toLowerCase()) // Exclude specified fields
        );
      })
      .sort((a: PropertyFieldRule, b: PropertyFieldRule) => {
        // Handle different naming conventions for order field
        const orderA = a.sortOrder !== undefined ? a.sortOrder : a.order || 0;
        const orderB = b.sortOrder !== undefined ? b.sortOrder : b.order || 0;
        return orderA - orderB;
      })
  );
}

/**
 * Check if a specific field is visible/enabled based on field rules
 * @param survey - Survey with field rules configuration
 * @param fieldKey - The field key to check (e.g., "comments", "amenities")
 * @returns boolean indicating if the field should be displayed
 */
export function isFieldVisible(
  survey: SurveyWithFieldRules | null | undefined,
  fieldKey: string,
): boolean {
  // Access field rules from survey configuration
  const fieldRules =
    survey?.surveyPropertyFieldRule?.propertyFieldRuleList ||
    survey?.propertyFieldRuleList;

  if (!fieldRules) {
    return false;
  }

  // Find the field rule for the specific field
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fieldRule = fieldRules.find(
    (rule: any) =>
      rule?.fieldKey?.toLowerCase() === fieldKey.toLowerCase() ||
      rule?.fieldLabel?.toLowerCase() === fieldKey.toLowerCase(),
  );

  if (!fieldRule) {
    return false;
  }

  // Check if field is active and not hidden
  return (
    (!("status" in fieldRule) || fieldRule.status === "ACTIVE") &&
    fieldRule.isHidden !== true
  );
}
